import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, PlayCircle } from "lucide-react";

const STATUS = [
  { k: "approved", label: "Aprovado" },
  { k: "scheduled", label: "Agendado" },
  { k: "paid", label: "Pago" },
  { k: "cancelled", label: "Cancelado" },
];

export default function AdminBonuses() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/bonuses").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (b, status) => {
    try {
      await api.patch(`/bonuses/${b.id}`, { status });
      toast.success("Estado atualizado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const runMonthly = async () => {
    try {
      const { data } = await api.post("/bonuses/generate-monthly");
      toast.success(`${data.count} prémios agendados para ${data.payment_date}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const currentMonth = new Date().toISOString().slice(0, 7);
  const [payoutMonth, setPayoutMonth] = useState(currentMonth);
  const downloadXlsx = () => window.open(`${API}/exports/bonuses.xlsx`, "_blank");
  const downloadPdf = () => window.open(`${API}/exports/bonuses.pdf`, "_blank");
  const downloadPayoutSheet = () => window.open(`${API}/exports/bonuses-monthly.xlsx?month=${payoutMonth}`, "_blank");

  const total = items.reduce((s, b) => s + (b.status === "paid" ? Number(b.amount || 0) : 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Financeiro</div>
          <h1 className="font-heading text-5xl mt-2">Prémios</h1>
          <p className="text-[var(--ink-soft)] mt-1">Total pago: <strong className="text-[var(--success)]">{total.toFixed(2)} €</strong></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={runMonthly} className="pill-btn pill-primary" data-testid="btn-generate-monthly"><PlayCircle className="w-4 h-4"/> Gerar mensalmente</button>
          <div className="flex items-center gap-1 border border-[var(--border)] rounded-2xl px-2 py-1 bg-white">
            <input type="month" value={payoutMonth} onChange={(e) => setPayoutMonth(e.target.value)} className="px-1 py-1 text-sm bg-transparent outline-none" data-testid="payout-month-input"/>
            <button onClick={downloadPayoutSheet} className="pill-btn pill-primary !py-1.5 !px-3" data-testid="btn-export-payout" title="Descarregar folha de pagamento mensal por referenciador">
              <FileSpreadsheet className="w-4 h-4"/> Folha Cartão Dá
            </button>
          </div>
          <button onClick={downloadXlsx} className="pill-btn pill-ghost" data-testid="btn-export-xlsx"><FileSpreadsheet className="w-4 h-4"/> Excel geral</button>
          <button onClick={downloadPdf} className="pill-btn pill-ghost" data-testid="btn-export-pdf"><FileText className="w-4 h-4"/> PDF</button>
        </div>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[var(--muted)] text-left">
              <th className="px-4 py-3">Cód. Ref.</th>
              <th className="px-4 py-3">Candidato</th>
              <th className="px-4 py-3">Vaga</th>
              <th className="px-4 py-3">Valor</th>
              <th className="px-4 py-3">Contratação</th>
              <th className="px-4 py-3">Pagamento</th>
              <th className="px-4 py-3">Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-[var(--border)]">
                <td className="px-4 py-3 font-mono text-xs">{b.referrer_code}</td>
                <td className="px-4 py-3 font-medium">{b.candidate_name}</td>
                <td className="px-4 py-3">{b.vacancy_title}</td>
                <td className="px-4 py-3 text-[var(--terracotta)] font-semibold">{Number(b.amount || 0).toFixed(2)} €</td>
                <td className="px-4 py-3 text-xs">{(b.hire_date || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-xs">{b.scheduled_payment_date}</td>
                <td className="px-4 py-3">
                  <select value={b.status} onChange={(e) => setStatus(b, e.target.value)} className="px-3 py-1.5 rounded-full bg-[var(--muted)] border border-[var(--border)] text-xs" data-testid={`bonus-status-${b.id}`}>
                    {STATUS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--ink-soft)]">Sem prémios ainda.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
